import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { CheckCircle, XCircle, SpinnerGap } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { tokenStore } from '../lib/tokenStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isRTL } = useLanguage();
  const auth = useAuth();

  const token = searchParams.get('token');
  const [status, setStatus] = useState('verifying'); // 'verifying' | 'success' | 'error'
  const [message, setMessage] = useState('');
  const calledRef = useRef(false); // guard against StrictMode double-invocation

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const run = async () => {
      if (!token) {
        setStatus('error');
        setMessage(isRTL ? 'الرابط غير صالح' : 'Invalid or missing token');
        return;
      }
      try {
        await axios.post(`${API}/auth/verify-email`, { token });
        setStatus('success');
        setMessage(isRTL
          ? 'تم تفعيل بريدك بنجاح! جاري تحويلك لتسجيل الدخول...'
          : 'Email verified! Redirecting you to sign in...');
        setTimeout(() => navigate('/login?verified=1'), 1800);
      } catch (e) {
        setStatus('error');
        const detail = e.response?.data?.detail;
        setMessage(typeof detail === 'string' ? detail : (isRTL
          ? 'الرابط منتهي أو غير صالح. اطلب رابطاً جديداً من صفحة تسجيل الدخول.'
          : 'The link is expired or invalid. Request a new one from the login page.'));
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 px-4" dir={isRTL ? 'rtl' : 'ltr'}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 text-center"
        data-testid="verify-email-card"
      >
        {status === 'verifying' && (
          <>
            <SpinnerGap size={56} className="mx-auto text-blue-600 animate-spin" />
            <h1 className="mt-4 text-xl font-bold text-slate-900">
              {isRTL ? 'جاري التحقق...' : 'Verifying your email...'}
            </h1>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle size={64} weight="fill" className="mx-auto text-emerald-500" data-testid="verify-success-icon" />
            <h1 className="mt-4 text-xl font-bold text-slate-900">
              {isRTL ? 'تم التفعيل!' : 'Verified!'}
            </h1>
            <p className="mt-2 text-slate-600 text-sm">{message}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <XCircle size={64} weight="fill" className="mx-auto text-red-500" data-testid="verify-error-icon" />
            <h1 className="mt-4 text-xl font-bold text-slate-900">
              {isRTL ? 'تعذر التفعيل' : 'Verification failed'}
            </h1>
            <p className="mt-2 text-slate-600 text-sm">{message}</p>
            <Link
              to="/login"
              className="inline-block mt-6 px-6 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition"
              data-testid="back-to-login-link"
            >
              {isRTL ? 'العودة لتسجيل الدخول' : 'Back to Login'}
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default VerifyEmail;
